import { supabase } from "@/integrations/supabase/client";
import { type Photographer, type Theme } from "@/data/photographers";

export type { Photographer, Theme };

const KNOWN_THEMES: Theme[] = [
  "portraits", "wedding", "family", "event",
  "business", "fashion", "editorial", "product", "real-estate",
  "sport", "boudoir", "street", "open",
];

function normalizeTheme(t: string): Theme {
  const lower = t.toLowerCase().trim();
  if (lower.startsWith("portrait")) return "portraits";
  if (lower.startsWith("wedding") || lower.startsWith("hochzeit")) return "wedding";
  if (lower.startsWith("famil")) return "family";
  if (lower.startsWith("event") || lower.startsWith("veranstalt")) return "event";
  if (lower.startsWith("business") || lower.startsWith("corporate") || lower.startsWith("headshot") || lower.startsWith("brand")) return "business";
  if (lower.startsWith("fashion") || lower.startsWith("mode")) return "fashion";
  if (lower.startsWith("editor") || lower.startsWith("redakt") || lower.startsWith("magazin")) return "editorial";
  if (lower.startsWith("product") || lower.startsWith("produkt")) return "product";
  if (lower.startsWith("real") || lower.startsWith("immobil") || lower.startsWith("architekt") || lower.startsWith("interior")) return "real-estate";
  if (lower.startsWith("sport")) return "sport";
  if (lower.startsWith("boudoir") || lower.startsWith("akt")) return "boudoir";
  if (lower.startsWith("street") || lower.startsWith("reportage")) return "street";
  if (KNOWN_THEMES.includes(lower as Theme)) return lower as Theme;
  return "open";
}

type DbRow = {
  id: string;
  slug: string;
  name: string;
  region: string | null;
  themes: string[] | null;
  bio: string | null;
  email: string | null;
  portrait_url: string | null;
  gallery_urls: string[] | null;
  country?: string | null;
  city?: string | null;
  price_from?: number | null;
  tfp?: boolean | null;
  price_on_request?: boolean | null;
  website_url?: string | null;
  instagram_handle?: string | null;
  portrait_position?: string | null;
  portrait_zoom?: number | null;
  gallery_themes?: string[] | null;
  gallery_titles?: string[] | null;
  gallery_captions?: string[] | null;
  shooting_settings?: string[] | null;
  languages?: string[] | null;
  founding_member?: boolean | null;
};


function rowToPhotographer(row: DbRow): Photographer {
  const portrait = row.portrait_url ?? "/placeholder.svg";
  const gallery = (row.gallery_urls ?? []).filter(Boolean);
  const rawThemes = row.gallery_themes ?? [];
  const galleryThemesAll = gallery.map((_, i) => {
    const raw = (rawThemes[i] ?? "open").toString();
    const parts = raw.split("|").map((s) => s.trim()).filter(Boolean);
    const ids = (parts.length ? parts : ["open"]).slice(0, 2).map((p) => normalizeTheme(p)) as Theme[];
    return ids;
  });
  const galleryThemes = galleryThemesAll.map((arr) => arr[0]) as Theme[];
  const rawTitles = row.gallery_titles ?? [];
  const rawCaptions = row.gallery_captions ?? [];
  const galleryTitles = gallery.map((_, i) => rawTitles[i] ?? "");
  const galleryCaptions = gallery.map((_, i) => rawCaptions[i] ?? "");
  return {
    id: row.slug,
    name: row.name,
    region: row.region ?? "",
    themes: (row.themes ?? []).map(normalizeTheme) as Theme[],
    priceFrom: row.price_from ?? 0,
    priceTo: 0,
    tfp: row.tfp ?? false,
    priceOnRequest: row.price_on_request ?? false,
    equipment: [],
    bio: row.bio ?? "",
    shoots: row.region ?? "Auf Anfrage",
    email: row.email ?? "",
    portrait,
    gallery: gallery.length ? gallery : [portrait],
    galleryThemes,
    galleryThemesAll,
    galleryTitles,
    galleryCaptions,
    country: row.country ?? null,
    city: row.city ?? null,
    websiteUrl: row.website_url ?? null,
    instagramHandle: row.instagram_handle ?? null,
    portraitPosition: row.portrait_position ?? "50% 50%",
    portraitZoom: row.portrait_zoom ?? 1,
    shootingSettings: row.shooting_settings ?? [],
    languages: row.languages ?? [],
    foundingMember: row.founding_member ?? false,
  };
}

export async function fetchAllPhotographers(): Promise<Photographer[]> {
  const { data, error } = await supabase
    .from("photographers")
    .select("id, slug, name, region, themes, bio, email, portrait_url, gallery_urls, gallery_themes, gallery_titles, gallery_captions, country, city, price_from, tfp, price_on_request, website_url, instagram_handle, portrait_position, portrait_zoom, shooting_settings, languages, founding_member")
    .eq("published", true)
    .order("created_at", { ascending: false });
  if (error) {
    console.error("[photographers]", error);
    return [];
  }
  return (data ?? []).map(rowToPhotographer);
}

export async function fetchPhotographerBySlug(slug: string): Promise<Photographer | null> {
  const { data, error } = await supabase
    .from("photographers")
    .select("id, slug, name, region, themes, bio, email, portrait_url, gallery_urls, gallery_themes, gallery_titles, gallery_captions, country, city, price_from, tfp, price_on_request, website_url, instagram_handle, portrait_position, portrait_zoom, shooting_settings, languages, founding_member")
    .eq("slug", slug)
    .eq("published", true)
    .maybeSingle();
  if (!error && data) return rowToPhotographer(data as DbRow);
  return null;
}